import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { useTranslation } from '@kostbar/i18n'
import type { SelectedProduct } from '@/features/products/components/product-sku-combobox'
import { ProductSkuCombobox } from '@/features/products/components/product-sku-combobox'
import { inventoryApi } from '@/api/endpoints/inventory'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { useCountSessionGuard } from '@/features/inventory-count/hooks/use-count-session-guard'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { ACTIONS } from '@/shared/constants/actions'
import { resolveInventoryEventTypeLabel } from '@/features/inventory/components/inventory-history-options'
import {
  stockInfoFromFields,
  useStockInfo,
} from '@/features/inventory/hooks/use-stock-info'
import { MovementQuantityInput } from '@/features/inventory/components/movement-quantity-input'
import { prepareMovementLines } from '@/features/inventory/lib/movement-lines'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

type MovementType = 'RECEIVE' | 'COUNT'

interface MovementLine {
  product: SelectedProduct | null
  quantity: string
}

interface Props {
  open: boolean
  onClose: () => void
  presetSku?: string
  presetProductName?: string
  defaultRestaurantId?: number
}

function StockDisplay({
  sku,
  restaurantId,
}: {
  sku: string
  restaurantId: number
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['inventory', sku, restaurantId],
    queryFn: () => inventoryApi.getOne(sku, restaurantId),
    enabled: !!sku && !!restaurantId,
  })

  if (isLoading) {
    return <span className="text-muted-foreground text-sm">...</span>
  }

  const available = data?.availableQty ?? 0
  return (
    <span className="text-sm font-medium">{formatStockDisplay(available)}</span>
  )
}

export function InventoryMovementModal({
  open,
  onClose,
  presetSku,
  presetProductName,
  defaultRestaurantId,
}: Props) {
  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items ?? []
  const { t: tInventory } = useTranslation('inventory')
  const { t: tCommon } = useTranslation('common')
  const { can } = usePermissions()
  const canReceive = can(ACTIONS.INVENTORY_RECEIVE)
  const canCount = can(ACTIONS.INVENTORY_COUNT)

  const [type, setType] = useState<MovementType>('RECEIVE')
  const [restaurantId, setRestaurantId] = useState<string>('')
  const [lines, setLines] = useState<Array<MovementLine>>([
    {
      product:
        presetSku && presetProductName
          ? { sku: presetSku, name: presetProductName }
          : null,
      quantity: '',
    },
  ])
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [autoFocusLineIndex, setAutoFocusLineIndex] = useState<number | null>(
    null,
  )

  const quantityRefs = useRef<Map<number, HTMLInputElement>>(new Map())
  // One key per submission (D6): kept across a retry of a submit that timed out, dropped once the
  // movement is posted, and never carried into the next dialog.
  const clientRefRef = useRef<string | null>(null)
  const queryClient = useQueryClient()

  // CHG-2026-09-stock-usage-and-price-twins (D9, D11): which lines may take decimals, and which
  // name a product without stock of its own. Unknown means a whole-number stock article.
  // A product picked in the combobox carries these facts itself; only a line without them (a
  // preset SKU) is looked up.
  const unknownSkus = useMemo(
    () =>
      lines
        .filter((l) => l.product && stockInfoFromFields(l.product) == null)
        .map((l) => l.product!.sku.trim())
        .filter((sku) => sku !== ''),
    [lines],
  )
  const lookedUp = useStockInfo(open ? unknownSkus : [])
  const stockInfo = useMemo(() => {
    const map = new Map(lookedUp)
    for (const line of lines) {
      const info = line.product ? stockInfoFromFields(line.product) : null
      if (line.product && info) map.set(line.product.sku.trim(), info)
    }
    return map
  }, [lines, lookedUp])

  const setQuantityRef = useCallback(
    (idx: number, el: HTMLInputElement | null) => {
      if (el) {
        quantityRefs.current.set(idx, el)
      } else {
        quantityRefs.current.delete(idx)
      }
    },
    [],
  )

  const focusQuantityInput = useCallback((idx: number) => {
    setTimeout(() => {
      quantityRefs.current.get(idx)?.focus()
    }, 50)
  }, [])

  useEffect(() => {
    if (open) {
      setType('RECEIVE')
      setRestaurantId(defaultRestaurantId ? String(defaultRestaurantId) : '')
      setLines([
        {
          product:
            presetSku && presetProductName
              ? { sku: presetSku, name: presetProductName }
              : null,
          quantity: '',
        },
      ])
      setReference('')
      setNote('')
      clientRefRef.current = null
    }
  }, [open, presetSku, presetProductName, defaultRestaurantId])

  const allowedTypes = useMemo<Array<MovementType>>(() => {
    const types: Array<MovementType> = []
    if (canReceive) types.push('RECEIVE')
    if (canCount) types.push('COUNT')
    return types
  }, [canReceive, canCount])

  useEffect(() => {
    if (allowedTypes.length === 0) return
    if (!allowedTypes.includes(type)) {
      setType(allowedTypes[0])
    }
  }, [allowedTypes, type])

  const createMovement = useMutation({
    mutationFn: async () => {
      const result = prepareMovementLines(lines, stockInfo)
      if (!result.ok) {
        throw new Error(
          result.error === 'noStock'
            ? tInventory('noStock.lineRefused', { name: result.name })
            : tInventory('toast.invalidNumber'),
        )
      }
      const prepared = result.lines
      if (prepared.length === 0) {
        throw new Error(tInventory('toast.noValidLines'))
      }
      if (!restaurantId) throw new Error(tInventory('toast.selectRestaurant'))
      if (type === 'COUNT' && !canCount)
        throw new Error(tInventory('toast.missingCountPermission'))
      if (type === 'RECEIVE' && !canReceive)
        throw new Error(tInventory('toast.missingReceivePermission'))
      if (!clientRefRef.current) clientRefRef.current = newIdempotencyKey()
      return inventoryApi.createMovement({
        type: type as any,
        restaurantId: Number(restaurantId),
        reference: reference || undefined,
        clientRef: clientRefRef.current,
        note: note || undefined,
        lines: prepared,
      })
    },
    onSuccess: () => {
      clientRefRef.current = null
      toast.success(tInventory('toast.movementPosted'))
      queryClient.invalidateQueries({ queryKey: ['inventory'] })
      queryClient.invalidateQueries({ queryKey: ['inventory-page'] })
      queryClient.invalidateQueries({ queryKey: ['inventory-history'] })
      onClose()
    },
    onError: (e: any) => {
      toast.error(e?.message || tInventory('toast.movementPostFailed'))
    },
  })

  const addLine = useCallback(() => {
    setLines((prev) => {
      const newIndex = prev.length
      setAutoFocusLineIndex(newIndex)
      return [...prev, { product: null, quantity: '' }]
    })
  }, [])

  const handleQuantityKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        const line = lines[idx]
        if (line.product && line.quantity) {
          addLine()
        }
      }
    },
    [lines, addLine],
  )

  const clearAutoFocus = useCallback(() => {
    setAutoFocusLineIndex(null)
  }, [])

  const removeLine = (idx: number) =>
    setLines((prev) => prev.filter((_, i) => i !== idx))

  const updateLineProduct = (idx: number, product: SelectedProduct | null) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, product } : l)))
  }

  const updateLineQuantity = (idx: number, quantity: string) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, quantity } : l)))
  }

  const lineInfo = (line: MovementLine) =>
    line.product ? stockInfo.get(line.product.sku.trim()) : undefined
  const lineNoStock = (line: MovementLine) => lineInfo(line)?.noStock ?? null

  const headerHelp = useMemo(() => {
    switch (type) {
      case 'RECEIVE':
        return tInventory('movement.helpReceive')
      case 'COUNT':
        return tInventory('movement.helpCount')
    }
  }, [type, tInventory])

  const restaurantSelected = !!restaurantId
  const countGuard = useCountSessionGuard(
    restaurantSelected ? Number(restaurantId) : null,
  )

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>{tInventory('movement.title')}</DialogTitle>
          <DialogDescription>{headerHelp}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-4">
          <div className="space-y-2">
            <Label>{tInventory('movement.typeLabel')}</Label>
            <Select
              value={type}
              onValueChange={(v) => setType(v as MovementType)}
            >
              <SelectTrigger className="min-w-[180px]">
                <SelectValue
                  placeholder={tInventory('movement.typePlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {allowedTypes.includes('RECEIVE') && (
                  <SelectItem value="RECEIVE">
                    {resolveInventoryEventTypeLabel(tInventory, 'RECEIVE')}
                  </SelectItem>
                )}
                {allowedTypes.includes('COUNT') && (
                  <SelectItem value="COUNT">
                    {resolveInventoryEventTypeLabel(tInventory, 'COUNT')}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {allowedTypes.length === 0 && (
              <div className="text-xs text-muted-foreground">
                {tInventory('movement.noPermissions')}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>{tInventory('movement.restaurantLabel')}</Label>
            <Select value={restaurantId} onValueChange={setRestaurantId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={tInventory('movement.restaurantPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {restaurants.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{tInventory('movement.referenceLabel')}</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={tInventory('movement.referencePlaceholder')}
            />
          </div>
        </div>

        <div className="space-y-3">
          <Label>{tInventory('movement.linesLabel')}</Label>
          <div className="border rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 gap-4 text-xs font-medium text-muted-foreground px-4 py-3 bg-muted/50">
              <div className="col-span-7">
                {tInventory('movement.linesProduct')}
              </div>
              <div className="col-span-2 text-center">
                {tInventory('movement.linesCurrentStock')}
              </div>
              <div className="col-span-2">
                {type === 'COUNT'
                  ? tInventory('movement.linesNewQty')
                  : tInventory('movement.linesQuantity')}
              </div>
              <div className="col-span-1" />
            </div>
            {lines.map((line, idx) => (
              <div
                key={idx}
                className="grid grid-cols-12 gap-4 items-center px-4 py-3 border-t"
              >
                <div className="col-span-7">
                  {line.product ? (
                    <div className="flex flex-col">
                      <span className="font-medium">{line.product.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {line.product.sku}
                      </span>
                      {lineNoStock(line) && (
                        <span className="text-xs font-medium text-destructive">
                          {tInventory(`noStock.${lineNoStock(line)}`)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <ProductSkuCombobox
                      value={null}
                      onChange={(p) => updateLineProduct(idx, p)}
                      onSelect={() => focusQuantityInput(idx)}
                      placeholder={tInventory('movement.searchPlaceholder')}
                      disabled={!restaurantSelected}
                      autoFocus={autoFocusLineIndex === idx}
                      onAutoFocusComplete={clearAutoFocus}
                    />
                  )}
                </div>
                <div className="col-span-2 text-center">
                  {line.product && restaurantSelected ? (
                    <StockDisplay
                      sku={line.product.sku}
                      restaurantId={Number(restaurantId)}
                    />
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </div>
                <MovementQuantityInput
                  inputRef={(el) => setQuantityRef(idx, el)}
                  value={line.quantity}
                  onChange={(value) => updateLineQuantity(idx, value)}
                  onKeyDown={(e) => handleQuantityKeyDown(e, idx)}
                  placeholder={tInventory('movement.quantityPlaceholder')}
                  disabled={
                    !restaurantSelected ||
                    !line.product ||
                    lineNoStock(line) != null
                  }
                  fractional={lineInfo(line)?.fractional === true}
                  unit={lineInfo(line)?.unit}
                />
                <div className="col-span-1 flex justify-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLine(idx)}
                    disabled={lines.length === 1}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <div className="px-4 py-3 border-t bg-muted/30">
              <Button
                variant="outline"
                size="sm"
                onClick={addLine}
                disabled={!restaurantSelected}
              >
                {tInventory('movement.addLine')}
              </Button>
              {!restaurantSelected && (
                <span className="ml-3 text-xs text-muted-foreground">
                  {tInventory('movement.selectRestaurantFirst')}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <Label>{tInventory('movement.noteLabel')}</Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={tInventory('movement.notePlaceholder')}
          />
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={createMovement.isPending}
          >
            {tCommon('cancel')}
          </Button>
          <Button
            onClick={() => void countGuard.guard(() => createMovement.mutate())}
            disabled={
              createMovement.isPending ||
              countGuard.checking ||
              !restaurantSelected ||
              (type === 'RECEIVE' ? !canReceive : !canCount) ||
              allowedTypes.length === 0
            }
          >
            {createMovement.isPending && <Spinner className="w-4 h-4 mr-2" />}
            {tInventory('movement.postMovement')}
          </Button>
        </DialogFooter>
      </DialogContent>
      {countGuard.dialog}
    </Dialog>
  )
}
